import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';

const LeadStatusChart = ({ statusData = [] }) => {
  // Default colors for different lead statuses
  const COLORS = {
    'new': '#2196f3',
    'contacted': '#ff9800',
    'qualified': '#4caf50',
    'proposal': '#9c27b0',
    'negotiation': '#f44336',
    'closed_won': '#8bc34a',
    'closed_lost': '#795548',
    'follow_up': '#607d8b',
  };

  // Transform data to ensure it's in the right format for recharts
  const chartData = statusData.map(item => ({
    name: item.status || item.name || 'Unknown',
    value: item.count || item.value || 0,
    status: item.status || item.name || 'unknown',
  }));

  // Get color for each status
  const getColor = (status) => {
    const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
    return COLORS[normalizedStatus] || '#9e9e9e';
  };

  // Custom label function for the pie chart
  const renderLabel = (entry) => {
    const percent = ((entry.value / chartData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1);
    return `${percent}%`;
  };

  // Custom tooltip content
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {data.payload.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Count: {data.payload.value}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Handle empty data state
  if (!chartData || chartData.length === 0) {
    return (
      <Card sx={{ height: '100%', minHeight: 400 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Lead Status Distribution
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 300,
              color: 'text.secondary',
            }}
          >
            <Typography variant="body1">
              No data available
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', minHeight: 400 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Lead Status Distribution
        </Typography>
        <Box sx={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderLabel}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={getColor(entry.status)} 
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="bottom" 
                height={36}
                formatter={(value, entry) => (
                  <span style={{ color: entry.color }}>
                    {value}
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        {chartData.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Total Leads: {chartData.reduce((sum, item) => sum + item.value, 0)}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default LeadStatusChart;